import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Trash2 } from 'lucide-react'
import Cta from '@/components/storefront/Cta'
import CheckoutSteps from '@/components/storefront/checkout/CheckoutSteps'
import { groupList, missingRequired, specSummary, useOptionGroups } from '@/lib/options'
import { money, priceRowLabel } from '@/lib/money'
import { useAuth } from '@/context/AuthContext'
import { useCart, pieces, linePriceRows, lineSubtotal, cartArtworks } from '@/context/CartContext'
import { cn } from '@/lib/cn'
import { incompleteRows, syncPersonalizations } from '@/lib/personalization'
import ProductImage from '@/components/order/ProductImage'
import { ArtworkThumb } from '@/components/order/ArtworkGallery'
import { asItemArtwork } from '@/lib/artwork'
import { Input } from '@/components/ui/Field'

export default function Cart() {
  const { user } = useAuth()
  const { items, selectedItems, update, remove, toggle, selectAll, selectedPieces } = useCart()
  const navigate = useNavigate()
  const optionGroups = useOptionGroups()

  const viewerTier =
    (user?.role === 'customer' ? user.tier?.name : undefined) ?? items[0]?.tier_prices[0]?.tier ?? 'member'
  const total = selectedItems.reduce(
    (sum, item) => sum + lineSubtotal(item, viewerTier, optionGroups.data),
    0,
  )
  const allSelected = items.length > 0 && selectedItems.length === items.length

  const checkout = () => {
    if (!user) navigate('/login', { state: { from: '/checkout' } })
    else navigate('/checkout')
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center px-5 py-24 text-center">
        <ShoppingCart className="size-12 text-foreground/20" />
        <h1 className="mt-4 text-2xl font-semibold">Your cart is empty</h1>
        <p className="mt-1 text-sm text-foreground/60">Configure a product and add it to your cart to get started.</p>
        <Cta to="/products" arrow className="mt-6">
          Browse products
        </Cta>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-10">
      <CheckoutSteps current={1} />

      <h1 className="mt-8 text-3xl font-semibold">Your cart</h1>
      <p className="mt-1 text-sm text-foreground/60">Prices shown at {viewerTier} tier - repriced to your tier at checkout</p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Tick what you want to buy now. Everything arrives ticked, so this
              row is a no-op for anyone who ignores it. It renders at every item
              count, including one, so the cart never changes shape. */}
          <div className="flex items-center justify-between gap-3 border-b border-foreground/10 pb-3">
            <label className="-m-2 flex cursor-pointer items-center gap-2 p-2">
              <input
                type="checkbox"
                className="size-4 shrink-0 accent-primary"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = selectedItems.length > 0 && !allSelected
                }}
                onChange={(e) => selectAll(e.target.checked)}
              />
              <span className="text-sm font-semibold">Select all</span>
            </label>
            <span className="text-xs text-foreground/60">
              {selectedItems.length} of {items.length} selected
            </span>
          </div>

          {items.map((item) => {
            const qty = pieces(item)
            const rows = linePriceRows(item, viewerTier, optionGroups.data)
            const specLine = specSummary(item.spec, optionGroups.data, ['piping', 'remark'])
            const missing = missingRequired(item.spec, item.required_options, optionGroups.data)
            return (
              <div key={item.uid} className={cn('box p-4', !item.selected && 'opacity-60')}>
                <div className="flex gap-4">
                  <label className="-m-2 flex cursor-pointer items-start p-2">
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-primary"
                      checked={!!item.selected}
                      onChange={() => toggle(item.uid)}
                      aria-label={`Buy ${item.name} now`}
                    />
                  </label>
                  <ProductImage product={item} className="size-20 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/p/${item.productId}`} className="font-bold hover:text-primary">
                          {item.name}
                        </Link>
                        <p className="text-xs text-foreground/60">{item.category}</p>
                      </div>
                      <button onClick={() => remove(item.uid)} className="text-foreground/40 hover:text-danger" title="Remove">
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                    {specLine && <p className="mt-1 text-xs text-foreground/60">{specLine}</p>}
                    {/* Options are only chosen on the product page, so the fix is to add the item again from there. */}
                    {missing.length > 0 && (
                      <p className="mt-1 text-xs font-semibold text-warning">
                        Choose a {groupList(missing)} for this item - remove it and add it again from the product page.
                      </p>
                    )}
                    {cartArtworks(item).length > 0 ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {cartArtworks(item).map((file, index) => (
                          <ArtworkThumb
                            key={`${file.artwork_key}-${index}`}
                            file={asItemArtwork(file)}
                            path={file.artwork_path}
                            className="size-12"
                          />
                        ))}
                        <span className="text-xs text-foreground/60">
                          {cartArtworks(item).length === 1 ? 'Artwork uploaded' : `${cartArtworks(item).length} artwork files uploaded`}
                        </span>
                      </div>
                    ) : (
                      item.artworkName && (
                        <p className="mt-1 text-xs text-warning">
                          Artwork: {item.artworkName} - file not uploaded yet, attach at checkout
                        </p>
                      )
                    )}
                    {/* Checkout insists on the font behind the names. */}
                    {item.personalizations.length > 0 &&
                      ((item.fonts?.length ?? 0) > 0 ? (
                        <p className="mt-1 text-xs text-foreground/60">
                          Font: {item.fonts!.map((file) => file.artwork_name).join(', ')}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-warning">Font for the names not uploaded yet - attach it at checkout</p>
                      ))}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-4 gap-2 border-t border-foreground/10 pt-3 sm:grid-cols-8">
                  {item.sizes.map((size) => (
                    <label key={size} className="text-center">
                      <span className="text-2xs font-bold uppercase text-foreground/50">{size}</span>
                      <Input
                        type="number"
                        min={0}
                        className="mt-0.5 h-8 text-center text-xs"
                        value={item.sizeMatrix[size] ?? ''}
                        placeholder="0"
                        onChange={(e) => {
                          const sizeMatrix = { ...item.sizeMatrix, [size]: Math.max(0, Number(e.target.value)) }
                          update(item.uid, {
                            sizeMatrix,
                            // Personalization is one row per piece, so qty edits
                            // resize the rows (typed names survive).
                            ...(item.personalizations.length > 0
                              ? { personalizations: syncPersonalizations(item.personalizations, sizeMatrix, item.sizes) }
                              : {}),
                          })
                        }}
                      />
                    </label>
                  ))}
                </div>

                {item.personalizations.length > 0 && (
                  <div className="mt-3 border-t border-foreground/10 pt-3">
                    <p className="text-xs font-bold">
                      Name &amp; number
                      {(item.personalization_price ?? 0) > 0 && (
                        <span className="ml-1 font-semibold text-primary">(+RM{item.personalization_price!.toFixed(2)}/pc)</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-2xs text-foreground/50">One row per piece - every row needs a name and a number.</p>
                    <div className="mt-2 space-y-2">
                      {item.personalizations.map((row, i) => (
                        <div key={i} className="grid grid-cols-[3.5rem_1fr] items-center gap-2 sm:flex">
                          <span className="rounded-lg bg-foreground/5 px-2 py-2 text-center text-xs font-bold uppercase sm:w-24">
                            {row.size}
                          </span>
                          <Input
                            className="order-last col-span-full sm:order-none"
                            placeholder={`Name ${i + 1}`}
                            value={row.name}
                            onChange={(e) =>
                              update(item.uid, {
                                personalizations: item.personalizations.map((r, n) => (n === i ? { ...r, name: e.target.value } : r)),
                              })
                            }
                          />
                          <Input
                            className="sm:w-24"
                            placeholder={`No ${i + 1}`}
                            value={row.number}
                            onChange={(e) =>
                              update(item.uid, {
                                personalizations: item.personalizations.map((r, n) => (n === i ? { ...r, number: e.target.value } : r)),
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                    {incompleteRows(item.personalizations).length > 0 && (
                      <p className="mt-2 text-xs font-semibold text-warning">
                        {incompleteRows(item.personalizations).length} row(s) still need a name and number before checkout.
                      </p>
                    )}
                  </div>
                )}

                <p className="mt-3 flex justify-between gap-3 border-t border-foreground/10 pt-2.5 text-sm">
                  <span className="text-foreground/60">
                    {rows.length > 1
                      ? rows.map((row) => (
                          <span key={row.group ?? ''} className="block">
                            {priceRowLabel({ group: row.group, qty: row.qty, price: row.unit })}
                          </span>
                        ))
                      : `${qty} pcs x ${money(rows[0]?.unit ?? 0)}`}
                  </span>
                  <span className="font-bold text-primary">{money(lineSubtotal(item, viewerTier, optionGroups.data))}</span>
                </p>
              </div>
            )
          })}
        </div>

        <div>
          <div className="sticky top-[72px] sm:top-[86px]">
          <div className="box p-5">
            <p className="text-xs font-bold uppercase tracking-wide text-foreground/50">Cart summary</p>
            <div className="mt-3 space-y-2 text-sm">
              <p className="flex justify-between">
                <span className="text-foreground/60">Items</span>
                <span className="font-semibold">
                  {selectedItems.length} of {items.length}
                </span>
              </p>
              <p className="flex justify-between border-b border-foreground/10 pb-2">
                <span className="text-foreground/60">Total pieces</span>
                <span className="font-semibold">{selectedPieces} pcs</span>
              </p>
              <p className="flex justify-between pt-1 text-base">
                <span className="font-bold">Total</span>
                <span className="font-extrabold text-primary">{money(total)}</span>
              </p>
            </div>
            <Cta onClick={checkout} disabled={selectedPieces === 0} fullWidth arrow className="mt-5">
              Proceed to checkout
            </Cta>
            {selectedItems.length === 0 && (
              <p className="mt-2 text-center text-xs text-warning">Select at least one item to continue.</p>
            )}
            {!user && <p className="mt-2 text-center text-xs text-foreground/50">You will be asked to sign in or register.</p>}
            {user && user.role !== 'customer' && (
              <p className="mt-2 text-center text-xs text-foreground/50">Checkout needs a customer account.</p>
            )}
            <Link to="/products" className="mt-3 block text-center text-xs font-semibold text-primary hover:underline">
              Continue shopping
            </Link>
          </div>
          </div>
        </div>
      </div>
    </div>
  )
}
